import { Button } from "../ui/button";
import {
    Building,
    Cloud,
    ConstructionIcon,
    GraduationCapIcon,
    LifeBuoy,
    LogOut,
    MoonStar,
    Settings,
    Sparkles,
    Sun,
    XIcon,
} from "lucide-react";
import ToneSettingsDialog from "../modals/tone-settings-dialog";
import { ReactComponent as TekkrLogoWhite } from "../../static/tekkr-logo-white.svg";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import React from "react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuPortal,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuthOptional } from "../../auth/auth-provider";
import { useLogout } from "../../auth/hooks/logout";
import { useColorScheme } from "../../lib/color-scheme";
import { OrgSelector, useOrgController, useOrgOptional } from "../../auth/org-provider";
import { config } from "../../lib/config";
import { cn, getInitialsForName } from "../../lib/utils";
import { localStorageKeys } from "../../lib/local-storage-keys";
import { TbBuildingPlus } from "react-icons/tb";
import { useIsEventMode } from "../hooks/use-is-event-mode";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { useLocalStorage } from "@uidotdev/usehooks";
import { OrgLogo } from "./org/org-logo";
import { GoogleDriveSyncIndicator } from "./google-drive/google-drive-sync-indicator";

function NavTooltip(props: React.PropsWithChildren<{ tooltipText: string }>) {
    const [hide, setHide] = useLocalStorage<boolean>(localStorageKeys.hideNavigationTooltips);
    if (hide) return <>{props.children}</>;
    return (
        <Tooltip delayDuration={250}>
            <TooltipTrigger asChild>{props.children}</TooltipTrigger>
            <TooltipContent className={"mx-4 my-3 max-w-xs font-medium text-muted-foreground"}>
                <div className={"flex flex-row items-start gap-0"}>
                    {props.tooltipText}
                    <XIcon
                        className={
                            "h-6 w-6 shrink-0 rounded-md p-1 transition-all hover:cursor-pointer hover:bg-accent"
                        }
                        onClick={() => setHide(true)}
                    />
                </div>
            </TooltipContent>
        </Tooltip>
    );
}

function NavButton(
    props: React.PropsWithChildren<{
        tooltipText: string;
        linkTo: string;
    }>
) {
    const { pathname } = useLocation();
    const isActive = pathname.startsWith(props.linkTo);
    return (
        <NavTooltip tooltipText={props.tooltipText}>
            <Button asChild variant={"ghost"} className={cn(isActive && "bg-accent")}>
                <Link to={props.linkTo}>{props.children}</Link>
            </Button>
        </NavTooltip>
    );
}

export interface NavbarNavigation {
    logoLink?: string;
    primaryItems: {
        title: string;
        path: string;
        tooltipText: string;
        icon: typeof XIcon;
    }[];
}

function Navbar(props: { navigation: NavbarNavigation; bannerOffset?: boolean }) {
    const { isDark, setIsDark } = useColorScheme();

    function toggleDarkMode() {
        setIsDark(!isDark);
    }

    const isEventMode = useIsEventMode();

    const auth = useAuthOptional();
    const orgController = useOrgController();
    const org = useOrgOptional();

    const navigate = useNavigate();
    const { pathname } = useLocation();

    const logout = useLogout();

    function selectOrg(id: string) {
        OrgSelector.setSelectedOrg(id);
        orgController.refetch();

        if (!pathname.startsWith("/org/")) {
            navigate("/projects");
        }
    }

    const logo = (
        <div className={"me-2 flex h-10 w-10 flex-col items-center justify-center rounded-lg bg-primary"}>
            <TekkrLogoWhite />
            {config.envTitle ? (
                <code className={"font-semibold text-primary-foreground"} style={{ fontSize: "0.5rem" }}>
                    {config.envTitle}
                </code>
            ) : null}
        </div>
    );

    return (
        <div
            className={cn(
                "z-50 duration-300 animate-in fade-in slide-in-from-top-2",
                isEventMode && "cursor-not-allowed"
            )}
        >
            <div className={cn("pointer-events-none", props.bannerOffset ? "h-28" : "h-16")}></div>
            <div
                className={cn(
                    "fixed left-0 right-0 z-40 h-16 border-b-2 border-b-accent bg-background px-8",
                    props.bannerOffset ? "top-12" : "top-0",
                    isEventMode && "pointer-events-none"
                )}
            >
                <div className={"flex items-center justify-between py-3"}>
                    <div className={"flex items-center gap-2"}>
                        {props.navigation.logoLink ? <Link to={props.navigation.logoLink}>{logo}</Link> : logo}
                        {props.navigation.primaryItems.map((item, i) => {
                            const Icon = item.icon;
                            return (
                                <NavButton key={i} tooltipText={item.tooltipText} linkTo={item.path}>
                                    <Icon className={"me-2 h-5 w-5"} />
                                    {item.title}
                                </NavButton>
                            );
                        })}
                    </div>
                    <div className={"grow"}></div>
                    <OrgLogo className={"me-6 h-8"} />
                    {auth && (
                        <div className={"flex items-center gap-4"}>
                            <GoogleDriveSyncIndicator />
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Avatar data-testid={"button-navbar-account"} className={"h-9 w-9 cursor-pointer"}>
                                        <AvatarImage src={auth.user?.photoURL ?? undefined} alt="Profile" />
                                        <AvatarFallback>
                                            {getInitialsForName(auth.user.displayName ?? "")}
                                        </AvatarFallback>
                                    </Avatar>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent className="me-4 mt-1 w-56">
                                    <DropdownMenuLabel
                                        onClick={() => {
                                            if (config.env !== "prod") auth.user.getIdToken().then(console.log);
                                        }}
                                    >
                                        {auth.user?.displayName}
                                        <br />
                                        <span className={"text-xs text-muted-foreground"}>{auth.user?.email}</span>
                                    </DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuGroup>
                                        <ToneSettingsDialog>
                                            <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                                                <Sparkles className="mr-2 h-4 w-4" />
                                                <span>Personalization</span>
                                            </DropdownMenuItem>
                                        </ToneSettingsDialog>
                                        <DropdownMenuItem onClick={() => toggleDarkMode()}>
                                            {isDark ? (
                                                <Sun className="mr-2 h-4 w-4" />
                                            ) : (
                                                <MoonStar className="mr-2 h-4 w-4" />
                                            )}
                                            <span>{isDark ? "Light" : "Dark"} mode</span>
                                        </DropdownMenuItem>
                                    </DropdownMenuGroup>
                                    {!!org && (
                                        <>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem onClick={() => navigate(`/org/${org.id}`)}>
                                                <div className={"flex flex-col"}>
                                                    <div className={"flex flex-row items-center"}>
                                                        <Building className="mr-2 h-4 w-4" />
                                                        <span>Manage organization</span>
                                                    </div>
                                                    <div className={"ml-6 text-sm text-muted-foreground"}>
                                                        {org?.name}
                                                    </div>
                                                </div>
                                            </DropdownMenuItem>
                                        </>
                                    )}
                                    {auth.account.orgs.length > 1 && (
                                        <DropdownMenuSub>
                                            <DropdownMenuSubTrigger>
                                                <span>Switch organization</span>
                                            </DropdownMenuSubTrigger>
                                            <DropdownMenuPortal>
                                                <DropdownMenuSubContent>
                                                    {auth.account.orgs.map((org) => (
                                                        <DropdownMenuItem
                                                            key={org.id}
                                                            onClick={() => selectOrg(org.id)}
                                                        >
                                                            <Building className={"me-2 h-4 w-4"} />
                                                            <span>{org.name}</span>
                                                        </DropdownMenuItem>
                                                    ))}
                                                </DropdownMenuSubContent>
                                            </DropdownMenuPortal>
                                        </DropdownMenuSub>
                                    )}
                                    <DropdownMenuSeparator />
                                    {(config.env !== "prod" || localStorage.getItem("tekkr-show-dev-options")) && (
                                        <DropdownMenuSub>
                                            <DropdownMenuSubTrigger className={"construction-tape-bg"}>
                                                <ConstructionIcon className={"me-2 h-4 w-4"} />
                                                <span>Dev options</span>
                                            </DropdownMenuSubTrigger>
                                            <DropdownMenuPortal>
                                                <DropdownMenuSubContent>
                                                    <NavbarDevOptions />
                                                </DropdownMenuSubContent>
                                            </DropdownMenuPortal>
                                        </DropdownMenuSub>
                                    )}
                                    <DropdownMenuItem onClick={() => navigate("/settings/google-drive")}>
                                        <Settings className="mr-2 h-4 w-4" />
                                        <span>Settings</span>
                                    </DropdownMenuItem>
                                    <Link to={"https://tekkr.io/contact-us"} target={"_blank"}>
                                        <DropdownMenuItem>
                                            <LifeBuoy className="mr-2 h-4 w-4" />
                                            <span>Support</span>
                                        </DropdownMenuItem>
                                    </Link>
                                    <DropdownMenuItem className={"hidden"} disabled>
                                        <Cloud className="mr-2 h-4 w-4" />
                                        <span>API</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => logout()}>
                                        <LogOut className="mr-2 h-4 w-4" />
                                        <span>Log out</span>
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default Navbar;

function NavbarDevOptions() {
    const resetTutorials = () => {
        const keys = [
            localStorageKeys.tutorialEditSegmentDismissed,
            localStorageKeys.discoveryThemeBannerDismissed,
            localStorageKeys.tekkrEmailBotDisclaimerDismissed,
            localStorageKeys.onboardingPlaybookProcessExplainerDismissed,
            localStorageKeys.hideNavigationTooltips,
            localStorageKeys.googleDriveTopBannerDismissed,
            localStorageKeys.googleDriveChatBannerDismissed,
            localStorageKeys.googleDriveSyncJobId,
        ];
        keys.forEach((key) => localStorage.removeItem(key));
    };
    return (
        <>
            <DropdownMenuItem onClick={resetTutorials}>
                <GraduationCapIcon className={"me-2 h-4 w-4"} />
                Reset tutorials
            </DropdownMenuItem>
            <Link to={"/create-organization"}>
                <DropdownMenuItem>
                    {TbBuildingPlus({ className: "me-2 h-4 w-4" })}
                    Create organization
                </DropdownMenuItem>
            </Link>
        </>
    );
}
